import apiClient from "./api-client";
import { openAuthenticatedTab } from "./open-authenticated";
import { lmsAuthPostMessageListen } from "./post-message";

export async function generateLmsApiToken(lms: string): Promise<{ success: true } | { success: false; error: string }> {
	if (await apiClient.lmsInfo.getTokenExists()) {
		return { success: true };
	}

	const instituteInfo = await apiClient.lmsInfo.getInstituteLmsInfo();
	if (!instituteInfo?.origin) {
		return { success: false, error: "Institute domain is not properly set." };
	} else if (!instituteInfo?.lmsApiClientId || instituteInfo.lmsApiClientId.trim() === "") {
		return { success: false, error: "Institute did not set up api key." };
	}

	/* Attempt to get the current hostname, and check if it is in the list of registered hostnames */
	const instituteUuid = (await apiClient.institute.getAccessControlInfo(instituteInfo.origin)).id;

	const { hostnames } = await apiClient.institute.getHostnames({ instituteUuid: instituteUuid });

	const windowHost = new URL(window.location.href).hostname;
	let verifiedHostname = undefined;
	if (hostnames.includes(windowHost)) {
		verifiedHostname = windowHost;
	}

	const tempSetCookieTabUrl = new URL(window.location.href);
	tempSetCookieTabUrl.searchParams.set("close", "close");
	openAuthenticatedTab(tempSetCookieTabUrl);
	await new Promise((r) => setTimeout(r, 1000));

	const redirectUrl = `/lms/${lms}/auth`;
	let scope: string | null = null;
	let url: URL | null = null;
	try {
		switch (lms) {
			case "canvas": {
				const scopes = [
					`url:GET|/api/v1/courses/:course_id/quizzes/:id`,
					`url:PUT|/api/v1/courses/:course_id/quizzes/:id`,
					`url:DELETE|/api/v1/courses/:course_id/quizzes/:id`,
					`url:GET|/api/quiz/v1/courses/:course_id/quizzes/:assignment_id`,
					`url:PATCH|/api/quiz/v1/courses/:course_id/quizzes/:assignment_id`,
					`url:DELETE|/api/quiz/v1/courses/:course_id/quizzes/:assignment_id`,
				];
				scope = scopes.join(` `);
				url = new URL(`https://${verifiedHostname ?? extractHostname(instituteInfo.origin)}/login/oauth2/auth`);
				break;
			}
			case "desire2learn": {
				const scopes = [
					`core:*:*`,
					`quizzing:quizzes:read`,
					`quizzing:quizzes:write`,
					`quizzing:quizzes:delete`,
					`quizzing:quizzes:create`,
					`organizations:organization:read`,
					`orgunits:course:read`
				];
				scope = scopes.join(` `);
				url = new URL(`https://auth.brightspace.com/oauth2/auth`);
				break;
			}
			case "BlackboardLearn": {
				scope = `*`;
				url = new URL(
					`https://${verifiedHostname ?? extractHostname(instituteInfo.origin)}/learn/api/public/v1/oauth2/authorizationcode`,
				);
				break;
			}
		}
	} catch (e) {
		console.error(e);
		return { success: false, error: "Invalid domain" };
	}
	if (!url || !scope) {
		return { success: false, error: "API access for LMS is not supported" };
	}
	url.searchParams.set("client_id", instituteInfo.lmsApiClientId);
	url.searchParams.set("response_type", "code");
	url.searchParams.set("redirect_uri", redirectUrl);
	url.searchParams.set("scope", scope);

	// Start authorization flow
	const newWin = window.open(url, "_blank", "noopener=false,noreferrer=false");
	if (!newWin || newWin.closed || typeof newWin.closed == "undefined") {
		return { success: false, error: "Failed to initiate OAuth flow" };
	}
	const authSuccess = await lmsAuthPostMessageListen(10000);
	if (!authSuccess.success) {
		return { success: false, error: authSuccess.error ?? "Failed." };
	}

	const tokenExists = await apiClient.lmsInfo.getTokenExists();
	if (!tokenExists) {
		return { success: false, error: "Failed to save OAuth token" };
	}
	return { success: true };
}

function extractHostname(url: string): string {
	if (!url.startsWith("http://") && !url.startsWith("https://")) {
		url = "https://" + url;
	}
	try {
		return new URL(url).hostname;
	} catch (error) {
		console.error("Invalid URL:", url);
		return "";
	}
}
